<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// D19: the card renders brain.display_name only — the AI user's sentinel
// email never reaches the UI.
const props = defineProps({
  brain: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['click']);

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();

const killSwitchDialogRef = ref(null);
const isEngagingKillSwitch = ref(false);

const presenceLabel = computed(() => {
  if (props.brain.kill_switched) {
    return t('AI_AGENTS.ROSTER.CARD.KILL_SWITCHED');
  }
  const key = (props.brain.presence || 'offline').toUpperCase();
  return t(`AI_AGENTS.ROSTER.CARD.PRESENCE.${key}`);
});

// M10/D35 condition: needs_attention is visually loud.
const presenceClass = computed(() => {
  if (props.brain.kill_switched) {
    return 'bg-n-ruby-9 text-white';
  }
  if (props.brain.presence === 'needs_attention') {
    return 'bg-n-ruby-9 text-white animate-pulse font-semibold';
  }
  if (props.brain.presence === 'online') {
    return 'bg-n-teal-3 text-n-teal-11';
  }
  return 'bg-n-slate-3 text-n-slate-11';
});

const versionLabel = computed(() => {
  return props.brain.latest_version_number
    ? t('AI_AGENTS.ROSTER.CARD.VERSION', {
        version: props.brain.latest_version_number,
      })
    : t('AI_AGENTS.ROSTER.CARD.DRAFT_ONLY');
});

const spendLabel = computed(() => {
  const { current_usd: current = 0, cap_usd: cap = 0 } =
    props.brain.spend || {};
  return t('AI_AGENTS.ROSTER.CARD.SPEND', { current, cap });
});

const deployChips = computed(() =>
  (props.brain.deploys || []).map(deploy => ({
    id: deploy.id,
    label: `${t(
      `AI_AGENTS.ROSTER.CARD.TARGET.${deploy.target.toUpperCase()}`
    )} · ${t(
      `AI_AGENTS.ROSTER.CARD.SYNC_STATE.${deploy.sync_state.toUpperCase()}`
    )}`,
    isHealthy: deploy.sync_state === 'synced',
    isBroken: ['drifted', 'error'].includes(deploy.sync_state),
  }))
);

const openKillSwitchDialog = () => {
  killSwitchDialogRef.value?.open();
};

const engageKillSwitch = async () => {
  isEngagingKillSwitch.value = true;
  try {
    await store.dispatch('aiAgentBrains/killSwitch', props.brain.id);
    useAlert(t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.SUCCESS'));
  } catch (error) {
    useAlert(t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.ERROR'));
  } finally {
    isEngagingKillSwitch.value = false;
    killSwitchDialogRef.value?.close();
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1 hover:outline-n-slate-6 cursor-pointer"
    role="button"
    @click="emit('click')"
  >
    <div class="flex items-center gap-3">
      <Avatar :name="brain.display_name" :size="40" rounded-full />
      <div class="flex flex-col min-w-0">
        <span class="text-base font-medium truncate text-n-slate-12">
          {{ brain.display_name }}
        </span>
        <span class="text-xs text-n-slate-11">{{ versionLabel }}</span>
      </div>
      <span
        class="ltr:ml-auto rtl:mr-auto px-2 py-0.5 text-xs rounded-full whitespace-nowrap"
        :class="presenceClass"
      >
        {{ presenceLabel }}
      </span>
    </div>

    <div class="flex flex-wrap gap-1.5">
      <span
        v-for="chip in deployChips"
        :key="chip.id"
        class="px-2 py-0.5 text-xs rounded-full"
        :class="{
          'bg-n-teal-3 text-n-teal-11': chip.isHealthy,
          'bg-n-ruby-3 text-n-ruby-11': chip.isBroken,
          'bg-n-slate-3 text-n-slate-11': !chip.isHealthy && !chip.isBroken,
        }"
      >
        {{ chip.label }}
      </span>
      <span
        v-if="!deployChips.length"
        class="px-2 py-0.5 text-xs rounded-full bg-n-slate-3 text-n-slate-11"
      >
        {{ t('AI_AGENTS.ROSTER.CARD.NO_DEPLOYS') }}
      </span>
    </div>

    <div class="flex items-center justify-between gap-2">
      <span class="text-xs text-n-slate-11">{{ spendLabel }}</span>
      <!-- M10: kill switch is the PRIMARY card control (admin-only). -->
      <Button
        v-if="isAdmin && !brain.kill_switched"
        :label="t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.BUTTON')"
        icon="i-lucide-power-off"
        color="ruby"
        size="sm"
        @click.stop="openKillSwitchDialog"
      />
      <span
        v-else-if="brain.kill_switched"
        class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-ruby-9 text-white"
      >
        {{ t('AI_AGENTS.ROSTER.CARD.KILL_SWITCHED') }}
      </span>
    </div>

    <Dialog
      ref="killSwitchDialogRef"
      type="alert"
      :title="
        t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.CONFIRM_TITLE', {
          name: brain.display_name,
        })
      "
      :description="t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.CONFIRM_MESSAGE')"
      :confirm-button-label="t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.CONFIRM')"
      :cancel-button-label="t('AI_AGENTS.ROSTER.CARD.KILL_SWITCH.CANCEL')"
      :is-loading="isEngagingKillSwitch"
      @confirm="engageKillSwitch"
    />
  </div>
</template>
